import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import FormWithProvider from 'components/forms/form/Form';
import { FormContextParts } from 'components/forms/form/Form.types';
import useAppModal from 'hooks/useAppModal';
import useAppUser from 'hooks/useAppUser';
import useNotify from 'hooks/useNotify';
import {
  NavigationItemPayload,
  UpdateDbsSettingsPayload,
} from 'models/DbsSettings';
import { getErrorMessage } from 'utils/axiosExceptionUtils';
import useFetchAgencyDbsConfig from '../../../useFetchAgencyDbsConfig';
import useFetchDbsSettings, {
  DBS_SETTINGS_BASE_QUERY_KEY,
} from '../../../useFetchDbsSettings';
import useUpdateDbsSettingsMutation from '../../../useUpdateDbsSettingsMutation';
import { navigationSettingsModalId } from './NavigationSettings.constants';
import {
  BOOK_NOW_NAVIGATION_NAME,
  BOOK_NOW_NAVIGATION_URL,
  getNavigationSettingsDefaultValues,
  getNavigationSettingsSchema,
  isBookNowNavigationItem,
  NavigationSettingsFormValues,
  normalizeNavigationUrl,
} from './NavigationSettings.schema';
import NavigationSettingsModalContent from './NavigationSettingsModalContent';

const NavigationSettingsModal = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const { agencyUid } = useAppUser();
  const { closeModal } = useAppModal();
  const { notifyError, notifySuccess } = useNotify();
  const { dbsSettings } = useFetchDbsSettings();
  const { agencyDbsConfig } = useFetchAgencyDbsConfig();
  const { mutateAsync: updateDbsSettings } = useUpdateDbsSettingsMutation();
  const queryClient = useQueryClient();

  const handleClose = () => closeModal(navigationSettingsModalId);

  const handleSubmit = async (
    formValues: NavigationSettingsFormValues,
    _event: unknown,
    {
      formState: { dirtyFields },
    }: FormContextParts<NavigationSettingsFormValues>,
  ) => {
    const bookNowItem = dbsSettings?.navigation.find(isBookNowNavigationItem);
    const bookNowTitle = formValues.bookNowTitle.trim() || null;
    const isBookNowTitleChanged =
      bookNowTitle !== (bookNowItem?.title?.trim() || null);

    if (!dirtyFields.navigation && !isBookNowTitleChanged) {
      handleClose();
      return;
    }

    const bookNowItems: NavigationItemPayload[] = [];

    // The endpoint replaces the whole list, so the Book Now item must always be sent.
    if (bookNowItem) {
      const { uid, name, url, openLinkInNewTab } = bookNowItem;
      bookNowItems.push({
        uid,
        name,
        url,
        openLinkInNewTab,
        title: bookNowTitle,
      });
    } else if (bookNowTitle) {
      bookNowItems.push({
        name: BOOK_NOW_NAVIGATION_NAME,
        url: BOOK_NOW_NAVIGATION_URL,
        openLinkInNewTab: false,
        title: bookNowTitle,
      });
    }

    const payload: UpdateDbsSettingsPayload = {
      navigation: [
        ...formValues.navigation.map(
          ({ uid, name, url, openLinkInNewTab }) => ({
            ...(uid ? { uid } : {}),
            name: name.trim(),
            url: normalizeNavigationUrl(url),
            openLinkInNewTab,
            title: null,
          }),
        ),
        ...bookNowItems,
      ],
    };

    try {
      await updateDbsSettings({ agencyUid: agencyUid!, payload });

      await queryClient.invalidateQueries([
        DBS_SETTINGS_BASE_QUERY_KEY,
        agencyUid,
      ]);

      notifySuccess(
        t('pageDirectBookingEngine.navigationSettings.updateSuccess'),
      );
      handleClose();
    } catch (error: any) {
      notifyError(
        getErrorMessage({
          ...error?.response?.data,
          baseKey: 'pageDirectBookingEngine.apiErrorCodes.',
          t,
        }),
      );
    }
  };

  return (
    <FormWithProvider
      defaultValues={getNavigationSettingsDefaultValues(dbsSettings)}
      dirtyChecker
      onSubmit={handleSubmit}
      schema={getNavigationSettingsSchema(t)}
    >
      <NavigationSettingsModalContent
        onClose={onClose}
        websiteUrl={agencyDbsConfig?.website}
      />
    </FormWithProvider>
  );
};

export default NavigationSettingsModal;
